import { useMemo } from 'react';

import type { AvailableOffer } from '../utils/phoneStatus/types';
import {
  getMatchedOffersByColorAndSize,
  getOffersWithInventory,
} from '../utils/phoneStatus/offerUtils';
import { InventoryItemType, InventoryStatus, ReserveStatus } from '../types';
import { FormattedPhone, InventoryItem } from '../utils/phoneStatus/types';
import getPhoneInventoryCacheKey from '../utils/phoneStatus/getPhoneInventoryCacheKey';
import useTmsApi, { TmsApiResponse } from './useTmsApi';
import { useActiveStoreId } from './useActiveStore';

type InventoryPayloadItem = {
  storeId: string;
  productSku: string;
  productType: InventoryItemType.phone;
};

// prepare inventory api payload given a set of available offers
// optional filtering by color and storage size
export const useInventoryPayload = (
  matchedOffers: AvailableOffer[],
): { request: InventoryPayloadItem[] } | null => {
  const activeStoreId = useActiveStoreId();

  return useMemo(() => {
    if (!activeStoreId) {
      return null;
    }
    return {
      request: matchedOffers.map(({ sku }) => ({
        productSku: sku,
        productType: InventoryItemType.phone,
        storeId: activeStoreId,
      })),
    };
  }, [matchedOffers, activeStoreId]);
};

type InventoryResponse = {
  reservableCarrierExists: boolean;
  offersWithInventory: Array<AvailableOffer & { status?: InventoryStatus }>;
} & Omit<TmsApiResponse<InventoryItem>, 'response'> & {
    response: InventoryItem[] | null;
    matchedOffers: AvailableOffer[];
    reservableOffer: AvailableOffer | null;
  };

const reservableStatus = [InventoryStatus.InStock, InventoryStatus.ShipToStore];

export const useInventoryApi = ({
  formattedPhone,
  reserveStatus,
  phoneColor,
  storageSize,
  disabled = false,
}: {
  formattedPhone: FormattedPhone;
  reserveStatus: ReserveStatus;
  phoneColor?: string;
  storageSize?: string;
  disabled?: boolean;
}): InventoryResponse => {
  const activeStoreId = useActiveStoreId();

  const matchedOffers = useMemo(
    () => getMatchedOffersByColorAndSize({
      availableOffers: formattedPhone.availableOffers,
      phoneColor,
      storageSize,
    }),
    [formattedPhone.availableOffers, phoneColor, storageSize],
  );

  const payload = useInventoryPayload(!disabled ? matchedOffers : []);

  const { response, error, loading } = useTmsApi<InventoryItem>(
    '/inventory/product-inventory',
    !disabled ? payload : null,
    'post',
    getPhoneInventoryCacheKey({
      storeId: activeStoreId ?? '',
      displayName: formattedPhone.displayName,
    }),
  );

  const inventoryItems = response?.response ?? null;

  const reservableInventoryItem = response?.response.find(
    ({ status, productSku }) => reservableStatus.includes(status)
      && matchedOffers.some(({ sku }) => sku === productSku),
  );

  const reservableOffer = reservableInventoryItem
    ? matchedOffers.find(
      ({ sku }) => sku === reservableInventoryItem.productSku,
    ) ?? null
    : null;

  const reservableCarrierExists = !!reservableInventoryItem;

  const offersWithInventory = useMemo(
    () => {
      if (inventoryItems) {
        return getOffersWithInventory({
          matchedOffers,
          inventoryItems,
          phoneReserveStatus: reserveStatus,
          reservableCarrierExists,
        });
      }
      return !loading ? [...matchedOffers] : [];
    },
    [
      matchedOffers,
      inventoryItems,
      reserveStatus,
      reservableCarrierExists,
      loading,
    ],
  );

  return {
    response: inventoryItems,
    error,
    loading,
    reservableCarrierExists,
    offersWithInventory,
    matchedOffers,
    reservableOffer,
  };
};
